/**
 * Jembatan dompet (мост кошелька) — сторона РЕБЁНКА.
 *
 * Когда страница открыта внутри <iframe> портала RMS, кошелёк держит родитель, а сюда
 * пробрасывается EIP-1193-провайдер через postMessage. Протокол "rms-idra/1":
 * _bmad-output/rms-idra-wallet-bridge/PROTOKOL.md
 *
 * Здесь только ТРАНСПОРТ. Белый список методов и проверка параметров транзакции —
 * на стороне родителя (он и только он говорит с кошельком). Дублировать фильтр тут
 * бессмысленно: код ребёнка и так под контролем того, кто его скомпрометировал.
 *
 * Вне рамки этот модуль не используется вообще — работает обычный @web3modal/ethers.
 */

export const PROTOKOL = 'rms-idra/1';

/** Точные origin родителя. Только полное равенство, никаких includes/endsWith/регулярок. */
export const ASAL_INDUK: readonly string[] = ['https://rms.co.id', 'https://rms.padigital.dev'];

/** Повторы рукопожатия, мс от старта: порядок загрузки родителя и рамки не определён. */
const JEDA_ULANG = [0, 250, 500, 1000, 2000, 2000, 2000, 2000];
/** Таймаут читающих методов. eth_sendTransaction — БЕЗ таймаута (человек думает сколько хочет). */
const BATAS_BACA = 30_000;

export interface JembatanProvider {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
  /** BrowserProvider (ethers v6) на события провайдера не подписывается — заглушки. */
  on(): void;
  removeListener(): void;
}

export type KeadaanJembatan =
  | { fase: 'menunggu' } // ждём ответа родителя
  | { fase: 'tanpa-induk' } // родителя нет → самостоятельный режим (Web3Modal)
  | { fase: 'siap'; akun: string | null; chainId: string | null; jembatan: JembatanProvider };

interface JendelaLike {
  self: unknown;
  parent: unknown;
  addEventListener(tipe: 'message', h: (e: any) => void): void;
  removeEventListener(tipe: 'message', h: (e: any) => void): void;
}

const jendelaBawaan = () => globalThis as unknown as JendelaLike;

/** Страница отрисована внутри рамки? Исключение трактуем как «да». */
export function dalamBingkai(jendela: JendelaLike = jendelaBawaan()): boolean {
  try {
    return jendela.parent !== jendela.self;
  } catch {
    return true;
  }
}

/** Стартовое состояние: вне рамки сразу «родителя нет» → WalletConnect рисуется без задержки. */
export function keadaanAwal(jendela: JendelaLike = jendelaBawaan()): KeadaanJembatan {
  return dalamBingkai(jendela) ? { fase: 'menunggu' } : { fase: 'tanpa-induk' };
}

function galat(code: number, message: string, data?: unknown): Error {
  return Object.assign(new Error(message), { code, ...(data === undefined ? {} : { data }) });
}

/** Человекочитаемое сообщение для кодов, которые приходят из моста/кошелька. */
export function pesanGalatJembatan(e: any): string | null {
  switch (e?.code) {
    case 4001:
      return 'Transaksi dibatalkan di dompet.';
    case 4100:
      return 'Akun dompet di portal berbeda. Sambungkan ulang di portal.';
    case 4200:
      return 'Portal menolak permintaan ini (metode tidak diizinkan).';
    case 4900:
      return 'Dompet portal terputus. Sambungkan ulang di portal.';
    case 4901:
      return 'Dompet tidak di jaringan Polygon. Pindahkan jaringan di portal.';
    default:
      return null;
  }
}

const akunNormal = (v: unknown): string | null =>
  typeof v === 'string' && v.startsWith('0x') ? v.toLowerCase() : null;

/**
 * Подписывается на сообщения родителя, ведёт рукопожатие и отдаёт состояние через onKeadaan.
 * Возвращает функцию отписки.
 */
export function pasangJembatan(
  onKeadaan: (k: KeadaanJembatan) => void,
  jendela: JendelaLike = jendelaBawaan(),
): () => void {
  const induk = jendela.parent as { postMessage(data: unknown, targetOrigin: string): void };
  const menunggu = new Map<string, { ok: (v: unknown) => void; gagal: (e: unknown) => void; timer?: any }>();
  const timers: any[] = [];
  let asalInduk: string | null = null; // origin, подтверждённый рукопожатием
  let akun: string | null = null;
  let chainId: string | null = null;
  let menyerah = false; // родитель не ответил вовремя → мост больше не включаем
  let mati = false;
  let nomor = 0;

  const batalkanSemua = (kode: number, pesan: string) => {
    for (const [, c] of menunggu) {
      if (c.timer) clearTimeout(c.timer);
      c.gagal(galat(kode, pesan));
    }
    menunggu.clear();
  };

  const jembatan: JembatanProvider = {
    request({ method, params }) {
      if (mati || !asalInduk) return Promise.reject(galat(4900, 'jembatan belum siap'));
      const id = 'p' + ++nomor;
      const tujuan = asalInduk;
      return new Promise<unknown>((ok, gagal) => {
        const catatan: { ok: typeof ok; gagal: typeof gagal; timer?: any } = { ok, gagal };
        menunggu.set(id, catatan);
        if (method !== 'eth_sendTransaction') {
          catatan.timer = setTimeout(() => {
            menunggu.delete(id);
            gagal(galat(-32603, `jembatan: tidak ada jawaban untuk ${method}`));
          }, BATAS_BACA);
        }
        induk.postMessage({ jembatan: PROTOKOL, tipe: 'permintaan', id, metode: method, params: params ?? [] }, tujuan);
      });
    },
    on() {},
    removeListener() {},
  };

  const beritahu = () => onKeadaan({ fase: 'siap', akun, chainId, jembatan });

  const dengar = (e: any) => {
    if (mati) return;
    if (!ASAL_INDUK.includes(e.origin)) return; // точное сравнение
    if (e.source !== induk) return; // тот же origin ≠ то же окно
    const d = e.data;
    if (!d || typeof d !== 'object' || d.jembatan !== PROTOKOL) return;

    if (d.tipe === 'induk-siap') {
      if (menyerah) return; // поздний ответ: локальный кошелёк уже мог подключиться
      for (const t of timers) clearTimeout(t);
      timers.length = 0;
      asalInduk = e.origin;
      batalkanSemua(4900, 'jembatan direset'); // хвосты предыдущей загрузки
      akun = akunNormal(d.akun);
      chainId = typeof d.chainId === 'string' ? d.chainId : null;
      beritahu();
      return;
    }

    if (!asalInduk) return; // до рукопожатия ничего не принимаем

    if (d.tipe === 'jawaban' && typeof d.id === 'string') {
      const c = menunggu.get(d.id);
      if (!c) return; // чужой/повторный id
      menunggu.delete(d.id);
      if (c.timer) clearTimeout(c.timer);
      if (d.galat) {
        c.gagal(
          galat(
            typeof d.galat.code === 'number' ? d.galat.code : -32603,
            String(d.galat.message ?? 'galat dompet'),
            d.galat.data,
          ),
        );
      } else {
        c.ok(d.hasil);
      }
      return;
    }

    if (d.tipe === 'peristiwa') {
      if (d.nama === 'accountsChanged') {
        akun = Array.isArray(d.data) ? akunNormal(d.data[0]) : null;
        if (!akun) batalkanSemua(4900, 'dompet terputus');
        beritahu();
      } else if (d.nama === 'chainChanged') {
        chainId = typeof d.data === 'string' ? d.data : null;
        beritahu();
      } else if (d.nama === 'disconnect') {
        akun = null;
        batalkanSemua(4900, 'dompet terputus');
        beritahu();
      }
    }
  };

  jendela.addEventListener('message', dengar);

  // siap шлём в ОБА разрешённых origin; браузер доставит только совпавшему. Не '*'.
  const kirimSiap = () => {
    if (asalInduk || mati) return;
    for (const asal of ASAL_INDUK) {
      try {
        induk.postMessage({ jembatan: PROTOKOL, tipe: 'siap' }, asal);
      } catch {
        /* родителя нет / origin не совпал — не наша забота */
      }
    }
  };
  kirimSiap();
  let tunda = 0;
  for (const jeda of JEDA_ULANG.slice(1)) {
    tunda += jeda;
    timers.push(setTimeout(kirimSiap, tunda));
  }
  timers.push(
    setTimeout(() => {
      if (asalInduk || mati) return;
      menyerah = true;
      onKeadaan({ fase: 'tanpa-induk' }); // деградация в самостоятельный режим
    }, tunda + 1000),
  );

  return () => {
    mati = true;
    for (const t of timers) clearTimeout(t);
    batalkanSemua(4900, 'jembatan ditutup');
    jendela.removeEventListener('message', dengar);
  };
}
